import React from 'react';
import { BringzzText, BringzzIcon } from "@bringzz/components";
import useRequest from 'hooks/useRequest';
import DefaultProductImage from 'assets/DefaultProductImage.png';

const BookmarksTab = ({ searchResults, onBookmarkRemoved }) => {
    const { sendRequest } = useRequest();
    const renderBookmarksView = () => {
        const bookmarkedProducts = searchResults?.products?.filter(p => p.isBookmarked) || [];
        const bookmarkedPharmacies = searchResults?.pharmacies?.filter(p => p.isBookmarked) || [];
        const bookmarkedPosts = searchResults?.posts?.filter(p => p.isBookmarked) || [];
        
        const totalBookmarks = bookmarkedProducts.length + bookmarkedPharmacies.length + bookmarkedPosts.length;
        
        if (totalBookmarks === 0) {
            return (
                <div className='px-6 mt-8 text-center'>
                    <BringzzIcon
                        size="48"
                        icon="IconBookmark"
                        folder="LineIcons"
                        className="text-gray-300 mx-auto mb-4"
                    />
                    <BringzzText tag='h3' className='text-gray-500 mb-2'>
                        No bookmarks found
                    </BringzzText>
                    <BringzzText tag='h4' className='text-gray-400'>
                        Bookmark items to see them here
                    </BringzzText>
                </div>
            );
        }
        
        return (
            <div className='px-6 mt-4'>
                {bookmarkedProducts.length > 0 && (
                    <div className='mb-6'>
                        <BringzzText tag='h2' className='mb-3'>Products ({bookmarkedProducts.length})</BringzzText>
                        <div className='grid grid-cols-2 gap-3'>
                            {bookmarkedProducts.map((product, index) => (
                                <div key={`bookmark-product-${product.id}`} className='flex flex-col items-center p-3 border rounded-lg relative'>
                                    <BringzzIcon
                                        icon="IconBookmarkSolid"
                                        folder="HeroIcons"
                                        size="20"
                                        className="absolute top-2 right-2 text-purple-500 cursor-pointer hover:text-red-600 transition-colors"
                                        onClick={async () => {
                                            try {
                                                await sendRequest({
                                                    method: 'POST',
                                                    url: '/module/bookmark/removeBookmark',
                                                    data: { elementUUID: product.integrationId, type: 1 }
                                                });
                                                onBookmarkRemoved?.();
                                            } catch (error) {
                                                console.error('Error removing product bookmark:', error);
                                            }
                                        }}
                                    />
                                    {product.productImage ? (
                                        <img src={product.productImage} className='h-16 w-16 mb-2 object-contain rounded' alt={product.productName} />
                                    ) : (
                                        <img src={DefaultProductImage} className='h-12 w-12 mb-2 object-contain rounded opacity-50' alt='No image' />
                                    )}
                                    <BringzzText tag='h5' className='text-center text-sm'>{product.productName}</BringzzText>
                                    {product.price && <BringzzText tag='h6' className='text-magic-lilac mt-1'>€{product.price}</BringzzText>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {bookmarkedPharmacies.length > 0 && (
                    <div className='mb-6'>
                        <BringzzText tag='h2' className='mb-3'>Pharmacies ({bookmarkedPharmacies.length})</BringzzText>
                        {bookmarkedPharmacies.map((pharmacy, index) => (
                            <div key={`bookmark-pharmacy-${pharmacy.id}`} className='flex items-center border-b py-2'>
                                {pharmacy.pharmacyPic ? (
                                    <img src={pharmacy.pharmacyPic} className='h-10 w-10 mr-2 rounded' alt={pharmacy.name} />
                                ) : (
                                    <img src={DefaultProductImage} className='h-8 w-8 mr-2 rounded opacity-50' alt='No image' />
                                )}
                                <div className='flex flex-col flex-grow'>
                                    <BringzzText tag='h4'>{pharmacy.name}</BringzzText>
                                    {pharmacy.telefon && <BringzzText tag='h5' className='text-gray-600'>{pharmacy.telefon}</BringzzText>}
                                    {pharmacy.city && <BringzzText tag='h6' className='text-gray-500'>{pharmacy.city}</BringzzText>}
                                </div>
                                <BringzzIcon
                                    icon="IconBookmarkSolid"
                                    folder="HeroIcons"
                                    size="20"
                                    className="text-purple-500 cursor-pointer hover:text-red-600 transition-colors"
                                    onClick={async () => {
                                        try {
                                            await sendRequest({
                                                method: 'POST',
                                                url: '/module/bookmark/removeBookmark',
                                                data: { elementUUID: pharmacy.integrationId, type: 2 }
                                            });
                                            onBookmarkRemoved?.();
                                        } catch (error) {
                                            console.error('Error removing pharmacy bookmark:', error);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
                
                {bookmarkedPosts.length > 0 && (
                    <div className='mb-6'>
                        <BringzzText tag='h2' className='mb-3'>Articles ({bookmarkedPosts.length})</BringzzText>
                        {bookmarkedPosts.map((post, index) => (
                            <div key={`bookmark-post-${post.id}`} className='flex items-center border-b py-3'>
                                {post.images?.[0]?.content ? (
                                    <img src={post.images[0].content} className='w-16 h-16 rounded mr-3 flex-shrink-0 object-cover' alt={post.headline} />
                                ) : (
                                    <div className='w-16 h-16 bg-gray-300 rounded mr-3 flex-shrink-0'></div>
                                )}
                                <div className='flex flex-col flex-grow'>
                                    <div className='flex items-center mb-1'>
                                        {post.user_pic ? (
                                            <img src={post.user_pic} className='w-5 h-5 rounded-full mr-2' alt={`${post.user_first_name} ${post.user_last_name}`} />
                                        ) : null}
                                        <BringzzText tag='h5' className='text-gray-600 mr-2'>{`${post.user_first_name || ''} ${post.user_last_name || ''}`.trim() || 'Author'}</BringzzText>
                                        <BringzzText tag='h5' className='text-gray-400'>• {new Date().toLocaleDateString()}</BringzzText>
                                    </div>
                                    <BringzzText tag='h4' className='font-medium'>{post.headline}</BringzzText>
                                </div>
                                <BringzzIcon
                                    icon="IconBookmarkSolid"
                                    folder="HeroIcons"
                                    size="20"
                                    className="text-purple-500 cursor-pointer hover:text-red-600 transition-colors"
                                    onClick={async () => {
                                        try {
                                            await sendRequest({
                                                method: 'POST',
                                                url: '/module/bookmark/removeBookmark',
                                                data: { elementUUID: post.integrationId, type: 0 }
                                            });
                                            onBookmarkRemoved?.();
                                        } catch (error) {
                                            console.error('Error removing post bookmark:', error);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return { renderBookmarksView };
};

export default BookmarksTab;